/**
 * Client-side CSV export and text download utilities.
 */
export function exportToCsv(filename, rows, columns) {
  if (!rows || rows.length === 0) {
    alert('Aucune donnée à exporter.');
    return;
  }
  const cols = columns || Object.keys(rows[0]);
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    cols.join(';'),
    ...rows.map((r) => cols.map((c) => escape(r[c])).join(';')),
  ].join('\r\n');

  // Add BOM for Excel fr-FR compatibility
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadTextFile(filename, content, mime = 'text/plain') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** "Downloads" a fake PDF-like text document for demo purposes. */
export function downloadAsFakePdf(filename, data) {
  const lines = [
    '=======================================',
    '  OFManager - Document généré automatiquement',
    '=======================================',
    '',
    ...Object.entries(data).map(([k, v]) => `${k.padEnd(20)} : ${v ?? '—'}`),
    '',
    `Généré le ${new Date().toLocaleString('fr-FR')}`,
  ].join('\n');
  downloadTextFile(filename.endsWith('.txt') ? filename : `${filename}.txt`, lines);
}

/**
 * Télécharge un PDF depuis une adresse authentifiée de l'API.
 *
 * **L'URL du blob ne se révoque pas dans la frame du clic** (2026-11-02, « le
 * PDF ne s'affiche pas »). Elle l'était juste après `a.click()`, donc détruite
 * avant que le navigateur n'ait commencé à lire le fichier : ça passe souvent,
 * ça échoue parfois, et quand ça échoue **rien ne se passe et rien ne le dit**
 * — le serveur a répondu 200, et l'utilisateur ne voit aucun fichier arriver.
 * Une minute suffit largement, et sans révocation du tout le blob resterait en
 * mémoire tant que l'onglet est ouvert.
 *
 * **Et le document est vide si la réponse n'est pas un PDF.** Une erreur de
 * l'API arrive ici en blob — du JSON, du HTML — et on enregistrait ce
 * message-là sous un nom en `.pdf` : un fichier qui ne s'ouvre pas. On lève, et
 * l'appelant dit que ça a échoué.
 */
export async function downloadPdf(api, url, filename) {
  const res = await api.get(url, { responseType: 'blob' });
  const recu = res.data;
  if (!recu || recu.size === 0) {
    throw new Error('Le serveur a renvoyé un document vide.');
  }
  if (recu.type && !recu.type.includes('pdf') && !recu.type.includes('octet-stream')) {
    throw new Error(`Le serveur n'a pas renvoyé un PDF (${recu.type}).`);
  }
  const blob = new Blob([recu], { type: 'application/pdf' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.setAttribute('download', filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(href), 60000);
}
